import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { BarChart3, Calendar as CalendarIcon, Download, TrendingUp, Users, Clock } from 'lucide-react';
import { format, subDays } from 'date-fns';

const ReportsDashboard = () => {
  const { api } = useAuth();
  const [reports, setReports] = useState(null);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('endDate', format(endDate, 'yyyy-MM-dd'));

      const [reportsRes, execRes] = await Promise.all([
        api.get(`/reports?${params.toString()}`),
        api.get('/executives')
      ]);

      setReports(reportsRes.data);
      setExecutives(execRes.data.executives);
    } catch (err) {
      toast.error('Failed to fetch reports');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleApplyFilter = () => {
    fetchReports();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const maxCount = reports?.byDay?.length > 0 
    ? Math.max(...reports.byDay.map(d => d.count))
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reports-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Reports</h1>
          <p className="text-zinc-400 mt-1">Analytics and performance metrics</p>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">From:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="bg-zinc-950 border-zinc-800" data-testid="report-start-date">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {startDate ? format(startDate, 'PP') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">To:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="bg-zinc-950 border-zinc-800" data-testid="report-end-date">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {endDate ? format(endDate, 'PP') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <Button onClick={handleApplyFilter} className="bg-indigo-600 hover:bg-indigo-500" data-testid="apply-report-filter">
              Apply Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {reports?.byDay?.reduce((sum, d) => sum + d.count, 0) || 0}
                </p>
                <p className="text-sm text-zinc-400">Total Recordings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 border border-green-500/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{formatDuration(reports?.avgDuration || 0)}</p>
                <p className="text-sm text-zinc-400">Avg Duration</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{reports?.byExecutive?.length || 0}</p>
                <p className="text-sm text-zinc-400">Active Executives</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Chart */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="border-b border-zinc-800 pb-4">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-400" />
              Recordings by Day
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {reports?.byDay?.length > 0 ? (
                reports.byDay.map((day, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">{format(new Date(day._id), 'EEE, MMM d')}</span>
                      <span className="text-zinc-300 font-mono">{day.count}</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 transition-all"
                        style={{ width: `${maxCount > 0 ? (day.count / maxCount) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-zinc-500 text-center py-8">No data for selected period</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* By Executive */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="border-b border-zinc-800 pb-4">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-400" />
              Recordings by Executive
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-4">
              {reports?.byExecutive?.length > 0 ? (
                reports.byExecutive.map((exec, index) => {
                  const maxExecCount = Math.max(...reports.byExecutive.map(e => e.count));
                  return (
                    <div key={index} className="p-3 bg-zinc-950/50 rounded-lg border border-zinc-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-zinc-300 font-medium">{exec._id.name}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-zinc-500">{formatDuration(exec.totalDuration)}</span>
                          <span className="text-indigo-400 font-mono">{exec.count}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
                          style={{ width: `${maxExecCount > 0 ? (exec.count / maxExecCount) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-zinc-500 text-center py-8">No data for selected period</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportsDashboard;
